/**
 * Dashboard page - main entry point for authenticated users
 */

import { Link } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { AcceptTerms } from './AcceptTerms'
import { GettingStarted } from './GettingStarted'
import { PendingApproval } from './PendingApproval'

// Icon components
const CheckCircleIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-8 w-8"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
)

const DocumentIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-8 w-8"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
    />
  </svg>
)

const CodeIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-8 w-8"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
  </svg>
)

const ShieldIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-10 w-10 text-white"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
    />
  </svg>
)

export const Dashboard = () => {
  const { user, isAuthenticated, login } = useAuth()

  // Not authenticated - show simple login prompt
  // (Main marketing is on scientistintheloop.com)
  if (!isAuthenticated) {
    return (
      <div className="min-h-[calc(100vh-200px)] bg-gradient-to-br from-slate-50 via-emerald-50 to-blue-50 -m-6 p-6">
        <div className="max-w-2xl mx-auto text-center py-16">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-emerald-500 to-blue-600 rounded-2xl shadow-lg mb-6">
            <ShieldIcon />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-4">FactChecker</h1>
          <p className="text-lg text-gray-600 mb-8">
            AI-powered fact verification with human-in-the-loop validation.
          </p>
          <button
            onClick={() => login()}
            className="inline-flex items-center px-8 py-3 bg-gradient-to-r from-emerald-500 to-blue-600 text-white font-medium rounded-lg shadow-lg hover:from-emerald-600 hover:to-blue-700 transition-all"
          >
            Sign In
          </button>
          <p className="mt-6 text-sm text-gray-500">
            Learn more at{' '}
            <a
              href="https://scientistintheloop.com/factcheck-mcp"
              className="text-emerald-600 hover:text-emerald-700"
            >
              scientistintheloop.com
            </a>
          </p>
        </div>
      </div>
    )
  }

  // Authenticated but pending approval
  if (user?.status === 'pending') {
    return <PendingApproval />
  }

  // Authenticated but rejected/suspended
  if (user?.status === 'rejected' || user?.status === 'suspended') {
    return (
      <div className="min-h-[calc(100vh-200px)] bg-gradient-to-br from-slate-50 via-red-50 to-orange-50 -m-6 p-6">
        <div className="max-w-2xl mx-auto text-center py-16">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Access Denied</h1>
          <p className="text-lg text-gray-600 mb-8">
            Your account access has been {user.status}. Please contact support if you believe this
            is an error.
          </p>
          <a
            href="mailto:sales@scientistintheloop.com"
            className="text-emerald-600 hover:text-emerald-700"
          >
            Contact Support
          </a>
        </div>
      </div>
    )
  }

  // Approved but hasn't accepted terms
  if (user?.status === 'approved' && !user?.terms_accepted) {
    return <AcceptTerms />
  }

  // Approved and terms accepted but hasn't completed onboarding
  if (user?.status === 'approved' && user?.terms_accepted && !user?.onboarding_completed) {
    return <GettingStarted />
  }

  // Approved - show full dashboard
  return (
    <div className="min-h-[calc(100vh-200px)] bg-gradient-to-br from-slate-50 via-emerald-50 to-blue-50 -m-6 p-6">
      <div className="max-w-5xl mx-auto">
        {/* Welcome Header */}
        <div className="text-center mb-10">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Welcome back, {user?.name || user?.email?.split('@')[0]}
          </h1>
          <p className="text-gray-600">What would you like to do today?</p>
        </div>

        {/* Action Cards */}
        <div className="grid md:grid-cols-3 gap-6">
          {/* View Results Card */}
          <Link to="/results" className="group">
            <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-100 text-center hover:shadow-lg transition-all transform hover:-translate-y-1 h-full">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-2xl flex items-center justify-center text-white mx-auto mb-6 group-hover:scale-110 transition-transform">
                <CheckCircleIcon />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">View Results</h2>
              <p className="text-gray-600 text-sm">
                Browse and review your fact verification results with detailed analysis.
              </p>
            </div>
          </Link>

          {/* Upload Documents Card */}
          <Link to="/upload" className="group">
            <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-100 text-center hover:shadow-lg transition-all transform hover:-translate-y-1 h-full">
              <div className="w-16 h-16 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-2xl flex items-center justify-center text-white mx-auto mb-6 group-hover:scale-110 transition-transform">
                <DocumentIcon />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">Upload Documents</h2>
              <p className="text-gray-600 text-sm">
                Upload trusted source documents for fact verification.
              </p>
            </div>
          </Link>

          {/* MCP Integration Card */}
          <Link to="/mcp-setup" className="group">
            <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-100 text-center hover:shadow-lg transition-all transform hover:-translate-y-1 h-full">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-400 to-pink-500 rounded-2xl flex items-center justify-center text-white mx-auto mb-6 group-hover:scale-110 transition-transform">
                <CodeIcon />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">MCP Integration</h2>
              <p className="text-gray-600 text-sm">
                Connect fact checking tools to Claude Desktop, Claude Code, and other AI assistants.
              </p>
            </div>
          </Link>
        </div>

        {/* Quick Tip Section */}
        <div className="mt-10 bg-white/70 backdrop-blur-sm rounded-xl p-6 border border-gray-100">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-emerald-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                  />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">Quick Tip</p>
                <p className="text-sm text-gray-600">
                  Use the MCP integration with Claude Desktop for seamless fact-checking in your
                  workflow.
                </p>
              </div>
            </div>
            <a
              href="https://modelcontextprotocol.io"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-emerald-600 hover:text-emerald-700 font-medium flex items-center"
            >
              Learn about MCP
              <svg className="ml-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                />
              </svg>
            </a>
          </div>
        </div>
      </div>
    </div>
  )
}
